import { useState } from 'react'
import { Loupe } from './Loupe'

/**
 * Une image du contenu — D-30, F1.3.
 *
 * **Elle se cite par son identifiant, jamais par une adresse.** Le composant
 * construit l'URL vers notre propre point d'entrée : une adresse distante
 * fuiterait l'IP du candidat vers un tiers, et ne serait pas cachable hors
 * ligne (ADR-04).
 *
 * **En économie de données, elle ne se charge pas d'elle-même.** Le candidat
 * voit sa description et un bouton. C'est le point du réglage : sur un forfait
 * au mégaoctet, un schéma qu'on n'a pas demandé coûte de l'argent qu'on n'a
 * pas. La description n'est pas un pis-aller — elle est obligatoire à
 * l'écriture (06 §10), donc toujours utilisable.
 *
 * **Elle s'ouvre en grand d'une tape.** Les schémas de cours portent des
 * légendes de huit points ; dans la colonne de texte d'un téléphone, elles ne
 * se lisent pas. Sans la loupe, le candidat n'a que deux issues — renoncer, ou
 * quitter l'application pour ouvrir son livre.
 */
export function MediaImage({
  id,
  alt,
  economieDeDonnees = false,
}: {
  id: string
  alt: string
  economieDeDonnees?: boolean | undefined
}) {
  const [demandee, setDemandee] = useState(!economieDeDonnees)
  const [absente, setAbsente] = useState(false)
  const [agrandie, setAgrandie] = useState(false)
  const source = `/api/v1/media/${id}`

  if (absente) {
    // Une image manquante ne doit pas casser l'énoncé qui l'entoure : le texte
    // alternatif prend sa place et la question reste répondable.
    return (
      <span className="italic" style={{ color: 'var(--texte-2)' }}>
        {alt}
      </span>
    )
  }

  if (!demandee) {
    return (
      <button
        type="button"
        onClick={() => {
          setDemandee(true)
        }}
        style={{ borderColor: 'var(--bordure)' }}
        className="my-2 flex w-full flex-col gap-1 rounded-2xl border px-4 py-3 text-left"
      >
        <span>{alt}</span>
        <span className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
          Afficher l’image
        </span>
      </button>
    )
  }

  return (
    <>
      {/* Un BOUTON et non une image cliquable : au clavier comme au lecteur
          d'écran, « agrandir » doit s'annoncer et s'atteindre par tabulation.
          Une `<img onClick>` ne le fait ni l'un ni l'autre. */}
      <button
        type="button"
        onClick={() => { setAgrandie(true); }}
        aria-label={`Agrandir : ${alt}`}
        className="my-2 block w-full cursor-zoom-in"
      >
        <img
          src={source}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => {
            setAbsente(true)
          }}
          style={{ borderColor: 'var(--bordure)' }}
          className="h-auto max-w-full rounded-2xl border"
        />
      </button>

      {agrandie && <Loupe src={source} alt={alt} onFermer={() => { setAgrandie(false); }} />}
    </>
  )
}
